import {createResource, getResource} from "./common"
import {Cents} from "./transaction"
export enum AuthorizationStatus {
	Authorized = "Authorized",
	Completed = "Completed",
	Canceled = "Canceled",
	Declined = "Declined",
}

export enum DeclineReason {
	CardVerificationValueFailed = "CardVerificationValueFailed",
	IncorrectPIN = "IncorrectPIN",
	ExceedsAmountLimit = "ExceedsAmountLimit",
	InsufficientFunds = "InsufficientFunds",
	RequestedFunctionNotSupported = "RequestedFunctionNotSupported",
	AllowablePINTriesExceeded = "AllowablePINTriesExceeded",
	SuspectedFraud = "SuspectedFraud",
	ClosedAccount = "ClosedAccount",
	DoNotHonor = "DoNotHonor",
	MerchantDenyList = "MerchantDenyList",
	CountryDenyList = "CountryDenyList",
	MccDenyList = "MccDenyList",
	CardholderBlocked = "CardholderBlocked",
	CardStolen = "CardStolen",
	RestrictedCard = "RestrictedCard",
	InsufficientFundsDuringStandIn = "InsufficientFundsDuringStandIn",
	ExceedsStandInLimit = "ExceedsStandInLimit",
}

export enum DeclinedBy {
	Visa = "Visa",
	Org = "Org",
	Unit = "Unit",
}

const reasons = new Map<DeclineReason, string>([
	[DeclineReason.CardVerificationValueFailed, "CVV verification failed"],
	[DeclineReason.IncorrectPIN, "PIN verification failed"],
	[DeclineReason.ExceedsAmountLimit, "Exceeds account/card limits"],
	[DeclineReason.InsufficientFunds, "Insufficient funds"],
	[DeclineReason.RequestedFunctionNotSupported, "Other"],
	[DeclineReason.AllowablePINTriesExceeded, "Too many PIN verification attempts"],
	[DeclineReason.SuspectedFraud, "Suspected Fraud"],
	[DeclineReason.ClosedAccount, "Closed Account"],
	[DeclineReason.DoNotHonor, "Other"],
	[DeclineReason.MerchantDenyList, "Merchant deny list"],
	[DeclineReason.CountryDenyList, "Country deny list"],
	[DeclineReason.MccDenyList, "Mcc deny list"],
	[DeclineReason.CardholderBlocked, "The card is inactive / blocked"],
	[DeclineReason.CardStolen, "The card was stolen"],
	[DeclineReason.RestrictedCard, "Restricted country"],
	[DeclineReason.InsufficientFundsDuringStandIn, "Insufficient funds during stand in"],
	[DeclineReason.ExceedsStandInLimit, "Exceeds stand in limit"],
])

export function getDeclineReason(reason: DeclineReason | undefined, status: AuthorizationStatus) {
	if (status !== AuthorizationStatus.Declined) {
		return "—"
	}

	if (!reason) {
		return "—"
	}

	return reasons.get(reason) ?? "Other"
}

/** Human-readable Declined By cell; only meaningful when status is Declined. */
export function getDeclinedByDisplay(status: AuthorizationStatus, declinedBy?: DeclinedBy) {
	if (status !== AuthorizationStatus.Declined) {
		return "—"
	}
	return declinedBy ?? "—"
}

export type Authorization = {
	type: "authorization"
	id: string
	attributes: {
		createdAt: Date
		amount: Cents
		cardLast4Digits: string
		merchant: {
			name: string
			type: string
			category: string
			location?: string
		}
		status: AuthorizationStatus
		declineReason?: DeclineReason
		declineDescription?: string
		declinedBy?: DeclinedBy
		recurring: boolean
	}
	relationships: {
		account?: {
			data: {
				type: string
				id: number | null
			}
		}
		customer?: {
			data: {
				type: string
				id: number | null
			}
		}
	}
}

export async function find(
	accessToken: string,
	offset: number,
	limit: number,
	accountId: string,
	statuses: AuthorizationStatus[],
	sort: string,
	fromAmount?: Cents | "",
	toAmount?: Cents | "",
	cardId?: string
) {
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			accountId,
			statuses: statuses && statuses.length > 0 ? statuses : null,
			includeNonAuthorized: true,
			fromAmount: fromAmount != "" ? fromAmount : null,
			toAmount: toAmount != "" ? toAmount : null,
			cardId: cardId ? cardId : null,
		},
		sort: sort,
	}

	return await getResource<Array<Authorization>>("authorizations", accessToken, query)
}

export async function get(accessToken: string, authorizationId: string, includeNonAuthorized: boolean) {
	return await getResource<Authorization>(`authorizations/${authorizationId}`, accessToken, {
		filter: {includeNonAuthorized},
	})
}

export async function release(accessToken: string, authorizationId: string) {
	return await createResource<Authorization>(`authorizations/${authorizationId}/release`, accessToken, {})
}
